'use client';

import { Menu, Text, toasterApi } from '@terra/ui-design-system';
import { HStack } from '@terra/ui-styled-system';
import { Portal } from '@ark-ui/react';
import {
  ArrowLeftRightIcon,
  ArrowUp02Icon,
  Copy01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import type { ReactNode } from 'react';
import { useChatTabs } from '../../../store/chatTabsStore';
import { useCreateNewChatTab } from '../actions/useCreateNewChatTab';

type ChatTabContextMenuProps = {
  tabId: string;
  threadId: string;
  children: ReactNode;
  contentContainerRef: React.RefObject<HTMLDivElement | null>;
};

export function ChatTabContextMenu({
  tabId,
  threadId,
  children,
  contentContainerRef,
}: ChatTabContextMenuProps) {
  const { createNewChatTab } = useCreateNewChatTab();
  const { chatTabs, clearChatTabs, closeTab, closeAll } = useChatTabs();
  const handleCloseSelf = () => {
    closeTab({ tabId });
  };

  const handleCloseOtherTabs = () => {
    closeAll({
      exceptTabId: tabId,
    });
  };

  const handleCloseAllTabs = () => {
    clearChatTabs();
    createNewChatTab({ contentContainerRef });
  };

  const handleCopyThreadId = async () => {
    try {
      if (window.electronAPI?.clipboard?.writeText) {
        const result = await window.electronAPI.clipboard.writeText(threadId);

        if (!result.success) {
          throw new Error(result.error ?? 'Failed to copy thread ID');
        }
      } else {
        await navigator.clipboard.writeText(threadId);
      }

      toasterApi.success({ title: 'Thread ID copied' });
    } catch (error) {
      console.error('Failed to copy thread id:', error);
      toasterApi.error({ title: 'Failed to copy thread ID' });
    }
  };

  const totalTabs = chatTabs.length;
  const showCloseTab = totalTabs > 1;
  const showCloseOthers = totalTabs > 1;
  const showCloseAll = totalTabs > 0;
  const showCloseGroup = showCloseTab || showCloseOthers || showCloseAll;
  const showSeparator = showCloseTab && (showCloseOthers || showCloseAll);

  return (
    <Menu.Root positioning={{ strategy: 'fixed' }}>
      <Menu.ContextTrigger asChild>{children}</Menu.ContextTrigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content minW="160px" py="1">
            <Menu.Item
              value="copy-thread-id"
              py="1"
              px="2"
              closeOnSelect={false}
              onClick={() => {
                void handleCopyThreadId();
              }}
            >
              <HStack gap="1.5">
                <Copy01Icon size={12} />
                <Text fontSize="xs">Copy Thread Id</Text>
              </HStack>
            </Menu.Item>
            {showCloseGroup && <Menu.Separator />}
            {showCloseTab && (
              <Menu.Item
                value="close-tab"
                py="1"
                px="2"
                onClick={handleCloseSelf}
              >
                <HStack gap="1.5">
                  <ArrowUp02Icon size={12} />
                  <Text fontSize="xs">Close Tab</Text>
                </HStack>
              </Menu.Item>
            )}
            {showSeparator && <Menu.Separator />}
            {showCloseOthers && (
              <Menu.Item
                value="close-others"
                py="1"
                px="2"
                onClick={handleCloseOtherTabs}
              >
                <HStack gap="1.5">
                  <ArrowLeftRightIcon size={12} />
                  <Text fontSize="xs">Close Others</Text>
                </HStack>
              </Menu.Item>
            )}
            {showCloseAll && (
              <Menu.Item
                value="close-all"
                py="1"
                px="2"
                onClick={handleCloseAllTabs}
              >
                <HStack gap="1.5">
                  <Cancel01Icon size={12} />
                  <Text fontSize="xs">Close All</Text>
                </HStack>
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
